import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Cpu, Speaker, Headphones, Tv, Radio, Waves, Sparkles, ShieldCheck, Truck, Wrench, Award, Volume2, Disc3, ShoppingBag } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import ProductCard from "../components/ProductCard";
import PromoSlider from "../components/PromoSlider";

const ICON_MAP = { Cpu, Speaker, Headphones, Tv, Radio, Waves, Volume2, Disc3, Sparkles };

const CATEGORIES_FALLBACK = [
  { name: "5.1 Home Theatre", icon: "Speaker" },
  { name: "7.1 Home Theatre", icon: "Speaker" },
  { name: "Dolby Atmos", icon: "Waves" },
  { name: "Stereo Hi-Fi", icon: "Disc3" },
  { name: "Soundbars", icon: "Volume2" },
  { name: "AV Receivers", icon: "Cpu" },
  { name: "Bookshelf Speakers", icon: "Speaker" },
  { name: "Floorstanding", icon: "Speaker" },
  { name: "Subwoofers", icon: "Radio" },
  { name: "Ceiling Speakers", icon: "Speaker" },
  { name: "Wireless Audio", icon: "Headphones" },
  { name: "Projectors", icon: "Tv" },
];

const BRANDS_FALLBACK = ["Klipsch","KEF","Polk Audio","Denon","Marantz","Yamaha","SVS","REL","Sonos","Monitor Audio","Q Acoustics","Paradigm","JBL","Sony","BenQ","Epson"];

const WHY = [
  { icon: ShieldCheck, title: "100% Genuine Products", d: "Sourced from authorised distributors only." },
  { icon: Award, title: "Authorised Dealers", d: "Full manufacturer warranty and support." },
  { icon: Wrench, title: "Professional Installation", d: "Certified engineers with 12+ years' experience." },
  { icon: Sparkles, title: "Expert Calibration", d: "Dirac, Audyssey, Anthem Room Correction." },
  { icon: Truck, title: "Pan-India Delivery", d: "White-glove logistics with insured shipping." },
  { icon: Speaker, title: "Custom Theatre Design", d: "Bespoke rooms tailored to your architecture." },
];

const SERVICES = [
  "Professional Home Theatre Design",
  "Dolby Atmos Installation",
  "Room Acoustic Calibration",
  "Acoustic Panel Treatment",
  "Projector & Screen Setup",
  "Cable Management",
  "Smart Home Integration",
  "Full Room Automation",
];

export default function Home() {
  const navigate = useNavigate();
  const { add } = useCart();
  const [featured, setFeatured] = useState([]);
  const [packages, setPackages] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [brands, setBrands] = useState(BRANDS_FALLBACK.map((n) => ({ name: n })));
  const [categories, setCategories] = useState(CATEGORIES_FALLBACK);
  const [tIndex, setTIndex] = useState(0);

  useEffect(() => {
    api.get("/products?featured=true").then((r) => setFeatured(r.data.slice(0, 8)));
    api.get("/packages").then((r) => setPackages(r.data));
    api.get("/testimonials").then((r) => setTestimonials(r.data));
    api.get("/brands").then((r) => { if (r.data && r.data.length) setBrands(r.data); }).catch(() => {});
    api.get("/categories").then((r) => { if (r.data && r.data.length) setCategories(r.data); }).catch(() => {});
    api.get("/gallery").then((r) => setGallery(r.data));
  }, []);

  useEffect(() => {
    if (testimonials.length === 0) return;
    const t = setInterval(() => setTIndex((i) => (i + 1) % testimonials.length), 6000);
    return () => clearInterval(t);
  }, [testimonials]);

  const heroImg = "https://images.unsplash.com/photo-1773867567872-3ad1fa481082?crop=entropy&cs=srgb&fm=jpg&w=2200&q=85";

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden grain on-dark">
        <div className="absolute inset-0">
          <motion.img initial={{ scale: 1.1 }} animate={{ scale: 1 }} transition={{ duration: 2.4, ease: "easeOut" }}
            src={heroImg} alt="Luxury home theatre" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-transparent"></div>
        </div>
        <div className="container-x relative z-10 pb-20 pt-32 md:pb-24 md:pt-40 w-full">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.3 }} className="max-w-3xl">
            <div className="num-badge mb-8" style={{ color: "#C9A34A" }}>
              <span style={{ background: "#C9A34A", opacity: 0.9 }}></span>
              <span style={{ color: "#C9A34A" }}>01 — Cinema · Reimagined</span>
            </div>
            <h1 className="font-display leading-[0.98] tracking-[-0.02em] text-white" style={{ fontSize: "clamp(2.5rem, 8vw, 8rem)", fontWeight: 400 }}>
              Experience <em className="gold-text" style={{ fontWeight: 500 }}>Cinema</em>
              <br />
              <span style={{ fontWeight: 300, fontStyle: "italic" }}>at Home.</span>
            </h1>
            <p className="mt-10 text-white/85 text-base md:text-lg max-w-xl leading-[1.75] font-light" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Reference-grade home theatres — Dolby Atmos, Hi-Fi audio, and hand-calibrated installation. Curated by <span className="italic" style={{ color: "#E8D68C" }}>HomeTheatres India</span>, a family showroom on Dispensary Road, Dehradun since 1985.
            </p>
            <div className="mt-12 flex flex-wrap gap-3 items-center">
              <Link to="/packages" className="btn-gold" data-testid="hero-explore-packages">Explore Packages <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/consultation" className="btn-ghost-outline" data-testid="hero-book-consultation" style={{ color: "#F5EFDF", borderColor: "rgba(245,239,223,0.35)", background: "transparent" }}>Book a Consultation</Link>
              <span className="text-[10px] uppercase tracking-[0.28em] ml-2 hidden md:inline" style={{ color: "rgba(245,239,223,0.5)", fontFamily: "'JetBrains Mono', monospace" }}>· 40 Years Curated</span>
            </div>
          </motion.div>
        </div>
        {/* Marquee credentials */}
        <div className="absolute bottom-0 inset-x-0 border-t border-white/10 bg-black/50 backdrop-blur-xl py-4 z-20">
          <div className="marquee">
            <div className="marquee-track text-xs uppercase tracking-[0.28em] text-white/60">
              {["Dolby Atmos Certified","IMAX Enhanced","THX Design","Bang & Olufsen Aesthetic","Authorised Klipsch Dealer","KEF Reference Partner","Sony 4K Laser","Marantz Premium"].concat(["Dolby Atmos Certified","IMAX Enhanced","THX Design","Bang & Olufsen Aesthetic","Authorised Klipsch Dealer","KEF Reference Partner","Sony 4K Laser","Marantz Premium"]).map((t, i) => (
                <span key={i} className="flex items-center gap-4"><span className="w-1 h-1 rounded-full" style={{ background: "#C99A2A" }}></span>{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <PromoSlider />

      {/* CATEGORIES */}
      <section className="py-16 md:py-32 container-x" data-testid="categories-section">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10 md:mb-16">
          <div className="max-w-xl">
            <div className="num-badge mb-3 md:mb-4"><span>02 — Featured Categories</span></div>
            <h2 className="font-display leading-[0.98] tracking-[-0.02em]" style={{ fontSize: "clamp(1.75rem, 5.5vw, 5rem)", fontWeight: 400 }}>A universe of <em className="gold-text" style={{ fontWeight: 500 }}>sonic craft.</em></h2>
          </div>
          <Link to="/shop" className="text-[11px] md:text-xs uppercase tracking-[0.2em] flex items-center gap-2 pb-2 border-b transition" style={{ borderColor: "rgba(15,12,8,0.2)", color: "var(--ink)", fontWeight: 600 }}>Browse everything <ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {categories.map((c) => {
            const Icon = ICON_MAP[c.icon] || Speaker;
            return (
              <Link key={c.name} to={`/shop?q=${encodeURIComponent(c.name)}`} className="card-luxury p-7 flex flex-col gap-5 min-h-[190px] group" data-testid={`cat-${c.name}`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-500 group-hover:scale-110" style={{ background: "linear-gradient(135deg, #F7F2E7, #EFE9D9)", border: "1px solid rgba(138,106,15,0.25)", color: "#8A6A0F" }}><Icon className="w-5 h-5" /></div>
                <div>
                  <div className="font-display text-[26px] leading-[1.1] tracking-[-0.01em]" style={{ color: "var(--ink)" }}>{c.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.22em] mt-3 flex items-center gap-1.5 transition" style={{ color: "rgba(15,12,8,0.5)", fontFamily: "'JetBrains Mono', monospace" }}>Discover <ArrowRight className="w-3 h-3" /></div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* BRANDS — editorial ribbon */}
      <section className="section-cream py-14 md:py-20 relative overflow-hidden" data-testid="brands-section">
        <div className="container-x">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8 md:mb-10">
            <div className="max-w-xl">
              <div className="num-badge mb-3"><span>03 — Authorised Partners</span></div>
              <h2 className="font-display leading-[1.05]" style={{ fontSize: "clamp(1.5rem, 3.5vw, 2.75rem)", fontWeight: 400 }}>Curators of the world&rsquo;s <em className="italic gold-text" style={{ fontWeight: 500 }}>finest audio houses</em></h2>
            </div>
            <Link to="/brands" className="text-[11px] md:text-xs uppercase tracking-[0.2em] flex items-center gap-2 pb-2 border-b transition" style={{ borderColor: "rgba(15,12,8,0.2)", color: "var(--ink)", fontWeight: 600 }}>All brands <ArrowRight className="w-3.5 h-3.5" /></Link>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-px rounded-2xl overflow-hidden" style={{ background: "rgba(15,12,8,0.09)", border: "1px solid rgba(15,12,8,0.09)" }}>
            {brands.map((b) => (
              <Link key={b.name} to={`/shop?q=${encodeURIComponent(b.name)}`} className="transition p-4 md:p-7 flex items-center justify-center text-center min-h-[80px] md:min-h-[100px] group" style={{ background: "#FDFBF6" }} data-testid={`brand-${b.name}`}>
                {b.logo_url ? (
                  <img
                    src={b.logo_url}
                    alt={b.name}
                    className="max-h-8 md:max-h-12 max-w-[100px] md:max-w-[140px] object-contain transition duration-500 group-hover:scale-105"
                    style={{ mixBlendMode: "multiply", filter: "grayscale(0.35)" }}
                    onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "inline"; }}
                  />
                ) : null}
                <span className="font-display text-sm md:text-lg tracking-[0.06em] md:tracking-[0.08em] transition" style={{ color: "rgba(15,12,8,0.65)", fontWeight: 500, display: b.logo_url ? "none" : "inline" }}>{b.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section className="py-16 md:py-32 container-x" data-testid="packages-section">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10 md:mb-16">
          <div className="max-w-xl">
            <div className="num-badge mb-3 md:mb-4"><span>04 — Curated Systems</span></div>
            <h2 className="font-display leading-[0.98] tracking-[-0.02em]" style={{ fontSize: "clamp(1.75rem, 5.5vw, 5rem)", fontWeight: 400 }}>Home Theatre <em className="gold-text" style={{ fontWeight: 500 }}>Packages</em></h2>
          </div>
          <Link to="/packages" className="text-[11px] md:text-xs uppercase tracking-[0.2em] flex items-center gap-2 pb-2 border-b transition" style={{ borderColor: "rgba(15,12,8,0.2)", color: "var(--ink)", fontWeight: 600 }}>View all <ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {packages.slice(0, 4).map((p) => (
            <div key={p.id} className="card-luxury brass-corners overflow-hidden group" data-testid={`package-${p.id}`}>
              <div className="grid md:grid-cols-2 gap-0">
                <div className="aspect-[4/3] md:aspect-auto overflow-hidden relative">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-[1.05] transition duration-1000" />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 60%, rgba(30,27,21,0.15) 100%)" }}></div>
                </div>
                <div className="p-8 md:p-9 flex flex-col gap-4">
                  <span className="num-badge text-[9px]"><span>{p.tier}</span></span>
                  <h3 className="font-display leading-[1.1] tracking-[-0.01em]" style={{ fontSize: "clamp(1.75rem, 2.6vw, 2.4rem)", fontWeight: 400 }}>{p.name}</h3>
                  <ul className="text-sm space-y-2 mt-1" style={{ color: "rgba(15,12,8,0.65)", fontFamily: "'Manrope', sans-serif" }}>
                    {p.highlights.slice(0, 4).map((h, i) => <li key={i} className="flex gap-2.5"><span style={{ color: "#8A6A0F", fontFamily: "'JetBrains Mono', monospace" }}>·</span>{h}</li>)}
                  </ul>
                  <div className="mt-auto pt-5 flex items-center justify-between border-t gap-4 flex-wrap" style={{ borderColor: "rgba(15,12,8,0.09)" }}>
                    <div>
                      {p.consultation_only ? (
                        <div className="text-sm text-gold">Consultation Required</div>
                      ) : (
                        <div className="text-2xl font-display">₹{p.price?.toLocaleString('en-IN')}</div>
                      )}
                    </div>
                    {p.consultation_only ? (
                      <Link to="/consultation" className="btn-ghost-outline text-xs px-5 py-2.5">Book Appointment</Link>
                    ) : (
                      <div className="flex gap-2">
                        <button onClick={() => add(p)} className="btn-ghost-outline text-xs px-4 py-2.5" data-testid={`pkg-add-${p.id}`}>
                          <ShoppingBag className="w-3.5 h-3.5" /> Add
                        </button>
                        <button onClick={() => { add(p); navigate("/cart"); }} className="btn-gold text-xs px-5 py-2.5" data-testid={`pkg-buy-${p.id}`}>
                          Buy Now
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="py-14 md:py-32 border-y border-black/5 bg-neutral-50">
        <div className="container-x">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              <div className="kicker mb-3">Individual Products</div>
              <h2 className="font-display text-4xl md:text-5xl">Handpicked <em className="not-italic gold-text">reference gear.</em></h2>
            </div>
            <Link to="/shop" className="text-sm text-white/60 hover:text-gold">Browse catalog →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {featured.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* INSTALLATION */}
      <section className="py-14 md:py-32 container-x" data-testid="installation-section">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative aspect-[4/3] rounded-3xl overflow-hidden">
            <img src="https://images.unsplash.com/photo-1585699324551-f6c309eedeca?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85" alt="Installation" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/60 to-transparent"></div>
          </div>
          <div>
            <div className="kicker mb-3">Installation Services</div>
            <h2 className="font-display text-4xl md:text-5xl mb-6">White-glove installation. <em className="not-italic gold-text">Reference calibration.</em></h2>
            <p className="text-white/60 leading-relaxed">Our engineers design and calibrate every room from the ground up — acoustics, speaker placement, Dirac Live measurements, and cinema-quality automation.</p>
            <div className="mt-8 grid grid-cols-2 gap-3">
              {SERVICES.map((s) => (
                <div key={s} className="glass rounded-xl px-4 py-3 text-sm text-white/70 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gold"></span>{s}
                </div>
              ))}
            </div>
            <Link to="/quote" className="btn-gold mt-8" data-testid="book-site-visit">Get a Quote <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-14 md:py-32 border-y border-black/5 bg-neutral-50">
        <div className="container-x">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="kicker mb-3">Why Batra Electronics</div>
            <h2 className="font-display text-4xl md:text-5xl">Crafted with the care of an <em className="not-italic gold-text">atelier.</em></h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {WHY.map(({ icon: Icon, title, d }) => (
              <div key={title} className="card-luxury p-8" data-testid={`why-${title}`}>
                <div className="w-11 h-11 rounded-xl glass flex items-center justify-center text-gold mb-5"><Icon className="w-5 h-5" /></div>
                <h3 className="font-display text-xl mb-2">{title}</h3>
                <p className="text-sm text-white/55 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-14 md:py-32 container-x" data-testid="testimonials-section">
        <div className="max-w-4xl mx-auto text-center">
          <div className="kicker mb-3">Client Voices</div>
          {testimonials.length > 0 && (
            <motion.div key={tIndex} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <p className="font-display text-2xl md:text-4xl leading-snug italic text-white/90">"{testimonials[tIndex]?.text}"</p>
              <div className="mt-8 flex items-center justify-center gap-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold to-gold-deep flex items-center justify-center font-medium text-black">{testimonials[tIndex]?.name?.[0]}</div>
                <div className="text-left">
                  <div className="font-medium">{testimonials[tIndex]?.name}</div>
                  <div className="text-xs text-white/50">{testimonials[tIndex]?.role} · {testimonials[tIndex]?.location}</div>
                </div>
              </div>
            </motion.div>
          )}
          <div className="mt-10 flex justify-center gap-2">
            {testimonials.map((_, i) => (
              <button key={i} onClick={() => setTIndex(i)} className={`h-1.5 rounded-full transition-all ${i === tIndex ? "w-8 bg-gold" : "w-3 bg-white/20"}`} data-testid={`test-dot-${i}`}></button>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="py-14 md:py-32 container-x" data-testid="gallery-section">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div>
            <div className="kicker mb-3">Gallery</div>
            <h2 className="font-display text-4xl md:text-5xl">Installations that <em className="not-italic gold-text">whisper luxury.</em></h2>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {gallery.map((g, i) => (
            <div key={g.id} className={`relative overflow-hidden rounded-2xl group ${i % 5 === 0 ? "md:row-span-2 aspect-[4/6]" : "aspect-[4/3]"}`} data-testid={`gallery-${g.id}`}>
              <img src={g.image} alt={g.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              <div className="absolute bottom-4 left-5 right-5">
                <div className="font-display text-lg">{g.title}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 container-x">
        <div className="relative overflow-hidden rounded-3xl grain on-dark">
          <img src="https://images.unsplash.com/photo-1614624532983-4ce03382d63d?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85" alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/20"></div>
          <div className="relative p-12 md:p-20 max-w-2xl">
            <div className="kicker mb-4" style={{ color: "#C99A2A" }}>Free Consultation</div>
            <h3 className="font-display text-4xl md:text-5xl leading-tight text-white" style={{ color: "#fff" }}>Ready to build your <em className="not-italic gold-text">dream theatre?</em></h3>
            <p className="mt-4 text-white/80 max-w-lg">Speak with our design team. We'll visit, measure, and craft a bespoke proposal — no obligation.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/quote" className="btn-gold">Get a Quote <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/packages" className="btn-ghost-outline" style={{ color: "#fff", borderColor: "rgba(255,255,255,0.35)", background: "transparent" }}>Browse Packages</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
